import {
  PDF_BORDER,
  PDF_FONT_SIZE,
  PDF_HEADER_BACKGROUND_COLOR,
  type ReportTableProps,
} from '@/components';
import {
  AccountingTypeLabelModifier,
  accountingType,
} from '@/features/master-table/models';
import {
  type ReportOrganizingTableFormat2Model,
  type ReportOrganizingTableFormat2PolicesModel,
  format2ColumnType,
  type projectDetailUrlTypeEnum,
} from '@/features/mold/models';
import { getProjectDetailPageUrl } from '@/features/project/_utils/project';
import { t } from '@/i18n';
import { formatValueByUnit } from '@/utils';
import type { CSSProperties } from 'react';
import type { z } from 'zod';
import { format2ColumnModifier, format2ValueConverter } from './get-csv';

type TableBodyElement = ReportTableProps['data'][number];

const headerStyle: CSSProperties = {
  fontSize: PDF_FONT_SIZE.MD,
  textAlign: 'center',
};

const getFormat2PdfHeader = ({
  year,
}: {
  year: number;
}): ReportTableProps['header'] => {
  const modifier = format2ColumnModifier({ year });
  // ratioは全体で100になるように調整してます
  return [
    { value: modifier.policyLevel1, ratio: 2, textStyle: headerStyle },
    { value: modifier.policyLevel2, ratio: 2, textStyle: headerStyle },
    { value: modifier.projectNumber, ratio: 4, textStyle: headerStyle },
    { value: modifier.name, ratio: 28, textStyle: headerStyle },
    {
      value: modifier.adminReformReviewOpinion,
      ratio: 17,
      textStyle: headerStyle,
    },
    {
      value: modifier.posteriorRequestedAmountTotal,
      ratio: 5,
      textStyle: headerStyle,
    },
    { value: modifier.organization, ratio: 27, textStyle: headerStyle },
    { value: modifier.accountingType, ratio: 15, textStyle: headerStyle },
  ];
};

const getFormat2PdfBody = ({
  policies,
  isDebugMode,
  projectDetailUrlType,
}: {
  policies: Array<z.infer<typeof ReportOrganizingTableFormat2PolicesModel>>;
  isDebugMode: boolean;
  projectDetailUrlType?: z.infer<typeof projectDetailUrlTypeEnum> | null;
}): ReportTableProps['data'] => {
  return policies.flatMap<TableBodyElement>((policyLevel1) => {
    return [
      {
        rowStyle: { backgroundColor: PDF_HEADER_BACKGROUND_COLOR },
        row: [
          {
            value: policyLevel1.policyName ?? t('not-set'),
            colSpan: 8,
          },
        ],
      },
      ...policyLevel1.policyMeasuresData.flatMap<TableBodyElement>(
        (policyLevel2) => [
          {
            rowStyle: { backgroundColor: PDF_HEADER_BACKGROUND_COLOR },
            row: [
              { value: '' },
              {
                value: policyLevel2.measureName ?? t('not-set'),
                colSpan: 7,
              },
            ],
          },
          ...policyLevel2.projectsData.flatMap<TableBodyElement>((content) => ({
            row: format2ColumnType.options.flatMap((v) => {
              switch (v) {
                case 'policyLevel1':
                case 'policyLevel2': {
                  return { value: '' };
                }
                case 'projectDetailPageUrl': {
                  return [];
                }
                case 'name': {
                  return {
                    type: 'link',
                    href: getProjectDetailPageUrl({
                      isPS: projectDetailUrlType === 'public',
                      projectId: content.id,
                    }),
                    value: content.name ?? '',
                  };
                }
                case 'posteriorRequestedAmountTotal': {
                  return {
                    value:
                      format2ValueConverter.posteriorRequestedAmountTotalValue({
                        value: content.posteriorRequestedAmountTotal,
                        type: 'pdf',
                        negativeCount: content.negativeRequestedAmountCount,
                        isDebugMode,
                        rawValue: content.rawPosteriorRequestedAmountTotal,
                      }),
                    textStyle: { textAlign: 'right' },
                  };
                }
                case 'accountingType': {
                  return {
                    value: format2ValueConverter.accountingType(
                      content.accountingType,
                      content.accountTitle,
                    ),
                  };
                }
                case 'adminReformReviewOpinion': {
                  return {
                    value: format2ValueConverter.adminReformReviewOpinion(
                      content.adminReformReviewOpinion,
                    ),
                  };
                }
                default: {
                  return { value: content[v] };
                }
              }
            }),
          })),
        ],
      ),
    ];
  });
};

const getFormat2PdfFooter = ({
  summary,
}: {
  summary: z.infer<typeof ReportOrganizingTableFormat2Model>['summary'];
}): ReportTableProps['footer'] => {
  const formattedSummary = [
    ...(typeof summary.generalAccountingTypeTotal === 'number'
      ? [
          {
            label: AccountingTypeLabelModifier[accountingType.Values.general],
            total: summary.generalAccountingTypeTotal,
          },
        ]
      : []),
    ...summary.specialAccountingType.map((accounting) => ({
      label: accounting.accountTitle,
      total: accounting.total,
    })),
  ].map((v) => {
    return {
      ...v,
      value: formatValueByUnit(v.total, {
        unit: 'thousand',
        operation: 'round',
      }),
    };
  });

  return [
    {
      row: [
        {
          value: t('total'),
          colSpan: 4,
          textStyle: { textAlign: 'center' },
          wrapperStyle: { borderTop: PDF_BORDER.NORMAL },
        },
        {
          type: 'multiple',
          wrapperStyle: { borderTop: PDF_BORDER.NORMAL },
          cell: {
            vertical: true,
            flexCells: formattedSummary.map((v) => ({
              value: v.label,
            })),
          },
        },
        {
          type: 'multiple',
          wrapperStyle: { borderTop: PDF_BORDER.NORMAL },
          cell: {
            vertical: true,
            flexCells: formattedSummary.map((v) => ({
              value: v.value,
              textStyle: { textAlign: 'right' },
            })),
          },
        },
        { value: '', wrapperStyle: { borderTop: PDF_BORDER.NORMAL } },
        { value: '', wrapperStyle: { borderTop: PDF_BORDER.NORMAL } },
      ],
    },
  ];
};

export const getFormat2Pdf = ({
  year,
  data,
  isDebugMode,
  projectDetailUrlType,
}: {
  year: number;
  data: z.infer<typeof ReportOrganizingTableFormat2Model>;
  isDebugMode: boolean;
  projectDetailUrlType?: z.infer<typeof projectDetailUrlTypeEnum> | null;
}): {
  header: ReportTableProps['header'];
  body: ReportTableProps['data'];
  footer: ReportTableProps['footer'];
} => {
  return {
    header: getFormat2PdfHeader({ year }),
    body: getFormat2PdfBody({
      policies: data.policies,
      isDebugMode,
      projectDetailUrlType,
    }),
    footer: getFormat2PdfFooter({ summary: data.summary }),
  };
};
